import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * FavoritesContext — wishlist for guests AND signed-in users.
 *
 * Source-of-truth model:
 *   - Guests: localStorage only.
 *   - Signed-in users: localStorage is hydrated from GET /api/me/favorites
 *     on auth resolve (so a returning customer's saved list survives device
 *     changes). Local + server are merged — neither is destroyed.
 *   - Toggle still fires POST/DELETE /api/me/favorites for signed-in users
 *     so the server stays in sync.
 */
const STORAGE_KEY = "companin_favorites";

const FavoritesContext = createContext({
  favorites: [],
  isFavorite: () => false,
  toggle: () => {},
  count: 0,
});

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  // Track previous user id so we can detect a logout transition
  // (was-signed-in → now-signed-out) and wipe the previous user's list.
  const prevUserIdRef = React.useRef(user?.user_id || null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
    } catch {
      // localStorage quota / disabled — silent.
    }
  }, [favorites]);

  // Hydrate from /api/me/favorites whenever the user signs in. On login we
  // union the server list with any local guest picks (so a guest's pre-login
  // hearts aren't lost) and POST the guest-only IDs so they persist server-
  // side. On logout, wipe local state — the next visitor should never inherit
  // the previous user's saved companions.
  useEffect(() => {
    const prevId = prevUserIdRef.current;
    const currId = user?.user_id || null;
    prevUserIdRef.current = currId;

    // Logout transition: previously signed in, now signed out → clear.
    if (prevId && !currId) {
      setFavorites([]);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // silent
      }
      return;
    }

    if (!user) return;
    let cancelled = false;
    api
      .get("/me/favorites")
      .then(({ data }) => {
        if (cancelled) return;
        const serverIds = Array.isArray(data)
          ? data.map((f) => f.provider_id || f).filter(Boolean)
          : [];
        setFavorites((curr) => {
          const localOnly = curr.filter((id) => !serverIds.includes(id));
          // Push guest-only picks up so the server catches up.
          localOnly.forEach((id) => {
            api.post("/me/favorites", { provider_id: id }).catch(() => {});
          });
          return Array.from(new Set([...serverIds, ...curr]));
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user]);

  const toggle = useCallback((providerId) => {
    setFavorites((curr) => {
      const willBeFavorite = !curr.includes(providerId);
      const next = willBeFavorite
        ? [...curr, providerId]
        : curr.filter((id) => id !== providerId);
      // Fire-and-forget backend sync — only succeeds when authenticated.
      if (willBeFavorite) {
        api.post("/me/favorites", { provider_id: providerId }).catch(() => {});
        // W19 #9 — confirmation toast on add only (removing is silent).
        toast.success("Added to favourites", {
          description: "You can rebook this companion anytime from your favourites page.",
          duration: 3500,
        });
      } else {
        api.delete(`/me/favorites/${providerId}`).catch(() => {});
      }
      return next;
    });
  }, []);

  const isFavorite = useCallback(
    (providerId) => favorites.includes(providerId),
    [favorites]
  );

  const value = useMemo(
    () => ({ favorites, isFavorite, toggle, count: favorites.length }),
    [favorites, isFavorite, toggle]
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export const useFavorites = () => useContext(FavoritesContext);
